import { FontIcon, Pivot, PivotItem, TooltipHost } from '@fluentui/react';
import { FCWithChildren } from 'react';
import styled from 'styled-components';
import { nameof } from '../../utils/stringUtils';
import ReportContainer from './ReportContainer';

const ReportHeader = styled.div`
  height: 32px;
  padding: 0 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 1.05rem;
  
  justify-items: center;
  align-items: center;

  display: grid;
  grid-auto-flow: row;
  grid-template-rows: 1fr;
  grid-auto-columns: 1fr;

  & > * {
    grid-row: 1;
  }

  & > :first-child {
    justify-self: start;
  }

  & > :not(:first-child):last-child {
    justify-self: end;
  }
`;
ReportHeader.displayName = nameof({ ReportHeader });

const NameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const IconButton = styled(FontIcon)`
  user-select: none;
  cursor: pointer;

  &:hover {
    color: ${p => p.theme.palette.themePrimary};
  }
`;

const StyledPivot = styled(Pivot)`
  * {
    line-height: normal;
    height: unset;
  }

  button {
    height: 32px;
  }
`;

const defaultTextRenderer = (text: string) => text;

interface BaseProps {
    num: number;
    iconName?: string;
    text: string;
    onRenderText?(text: string): JSX.Element;
    extraControl?: JSX.Element;
}

type EditProps = {
    showEdit: true;
    onEditClick(): void;
} | {
    showEdit?: false;
    onEditClick?(): void;
};

type TabSelectorProps = {
    tabs: { key: string, text: string }[];
    selectedTab: string;
    onTabChanged(key: string): void;
} | {
    tabs?: undefined;
    selectedTab?: string;
    onTabChanged?(key: string): void;
};

const Report: FCWithChildren<BaseProps & EditProps & TabSelectorProps> = ({ num, iconName, text, onRenderText, showEdit, onEditClick, children, tabs, selectedTab, onTabChanged, extraControl }) => (
    <ReportContainer num={num}>
        <ReportHeader>
            <NameContainer>
                {iconName && <FontIcon iconName={iconName} />}
                <span className="report-title">{(onRenderText ?? defaultTextRenderer)(text)}</span>
                {
                    showEdit &&
                    <TooltipHost content="Edit thresholds" styles={{ root: { display: 'inline-flex' } }}>
                        <IconButton iconName="BarChartVerticalEdit" onClick={onEditClick} />
                    </TooltipHost>
                }
            </NameContainer>
            {
                tabs
                    ? (
                        <StyledPivot headersOnly selectedKey={selectedTab} onLinkClick={(item) => onTabChanged(item?.props.itemKey ?? tabs[1].key)}>
                            {tabs.map((t, i) => <PivotItem key={i} itemKey={t.key} headerText={t.text} />)}
                        </StyledPivot>
                    )
                    : <></>
            }
            {extraControl ?? <></>}
        </ReportHeader>
        {children}
    </ReportContainer>
);

Report.displayName = nameof({ Report });
export default Report;